import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle, PencilSimple, ArrowClockwise, ShieldCheck, CreditCard, Download } from "@phosphor-icons/react";
import PublicFooter from "@/components/PublicFooter";

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_francophone-ops/artifacts/1lh4l6xi_3%20%281%29.PNG";
const publicApi = axios.create({ baseURL: API });

const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
};
const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function SignClientAgreement() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [typedName, setTypedName] = useState("");
  const [agree, setAgree] = useState(false);
  const [refundInitials, setRefundInitials] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(null);
  // Auto-redirect countdown to Stripe checkout after signing (declared here so hooks
  // run in the same order on every render — DO NOT move below any early return).
  const [redirectIn, setRedirectIn] = useState(null); // seconds remaining
  useEffect(() => {
    if (!signed || !signed.stripe_url) return;
    // Skip auto-redirect when the client is REOPENING an already-signed link
    // (they may have closed the tab intentionally). Skip when deposit is already paid.
    if (signed.reopened || signed.deposit_paid) return;
    setRedirectIn(5);
    const interval = setInterval(() => {
      setRedirectIn((s) => {
        if (s === null) return null;
        if (s <= 1) {
          clearInterval(interval);
          window.location.href = signed.stripe_url;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [signed]);

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const emptyRef = useRef(true);

  useEffect(() => {
    publicApi.get(`/public/sign/client/${token}`)
      .then((r) => {
        setState({ loading: false, data: r.data, error: null });
        // Pre-populate the "signed" screen when reopening a link that was
        // already signed — this is the state-aware Contract-to-Deposit flow.
        if (r.data.already_signed) {
          setSigned({
            signed_at: r.data.signed_at,
            contract_number: r.data.contract_number,
            stripe_url: r.data.stripe_url,
            deposit_paid: r.data.deposit_paid,
            deposit_paid_at: r.data.deposit_paid_at,
            is_deposit: r.data.is_deposit,
            charge_now: r.data.charge_now,
            reopened: true,
          });
        }
      })
      .catch((e) => setState({ loading: false, data: null, error: e?.response?.data?.detail || "Invalid or expired signing link" }));
  }, [token]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * 2; c.height = rect.height * 2;
    const ctx = c.getContext("2d");
    ctx.scale(2, 2); ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0F172A";
    const pos = (e) => { const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: t.clientX - r.left, y: t.clientY - r.top }; };
    const start = (e) => { e.preventDefault(); drawingRef.current = true; emptyRef.current = false; const {x,y}=pos(e); ctx.beginPath(); ctx.moveTo(x,y); };
    const move = (e) => { if (!drawingRef.current) return; e.preventDefault(); const {x,y}=pos(e); ctx.lineTo(x,y); ctx.stroke(); };
    const end = () => { drawingRef.current = false; };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    c.addEventListener("touchstart", start, { passive: false });
    c.addEventListener("touchmove", move, { passive: false });
    c.addEventListener("touchend", end);
    return () => {
      c.removeEventListener("mousedown", start); c.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      c.removeEventListener("touchstart", start); c.removeEventListener("touchmove", move); c.removeEventListener("touchend", end);
    };
  }, [state.data]);

  const clearCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height); emptyRef.current = true;
  };

  const submit = async () => {
    if (!agree) return toast.error("You must agree to the terms");
    if (typedName.trim().length < 3) return toast.error("Please type your full legal name");
    if (refundInitials.trim().length < 2) return toast.error("Please initial the Cancellation & Refund Policy section");
    if (emptyRef.current) return toast.error("Please draw your signature");
    setSubmitting(true);
    try {
      const png = canvasRef.current.toDataURL("image/png");
      const r = await publicApi.post(`/public/sign/client/${token}`, {
        typed_name: typedName.trim(), signature_png_b64: png, agree: true,
        refund_policy_initials: refundInitials.trim().toUpperCase(),
      });
      setSigned(r.data);
      toast.success("Agreement signed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed");
    } finally { setSubmitting(false); }
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading agreement…</div>;
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 shadow-sm text-center" data-testid="sign-client-error">
          <div className="text-lg font-semibold text-slate-900 mb-2">Link not valid</div>
          <div className="text-sm text-slate-500">{state.error}</div>
        </div>
      </div>
    );
  }

  const d = state.data;

  // State-aware post-sign screen — handles three sub-states:
  //   1) Deposit already paid → "Booking confirmed" screen (dead-end proof).
  //   2) Reopened after signing but unpaid → prominent "Pay deposit" screen, no auto-redirect.
  //   3) Freshly signed just now → success screen + 5s auto-redirect to Stripe.
  if (signed) {
    const payUrl = signed.stripe_url || d.stripe_url;
    const downloadUrl = `${API}/public/sign/client/${token}/download?fmt=pdf`;
    const chargeNow = signed.charge_now ?? d.charge_now ?? d.amount;
    const isDeposit = signed.is_deposit ?? d.is_deposit;
    const payLabel = isDeposit ? "Pay deposit" : "Pay in full";

    // ── State 1: booking already confirmed (deposit paid) ─────────────────
    if (signed.deposit_paid) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
          <div className="max-w-md w-full bg-white border border-emerald-200 rounded-xl p-8 shadow-sm text-center" data-testid="sign-client-booking-confirmed">
            <div className="mx-auto mb-3 h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle size={32} weight="fill" className="text-emerald-600" />
            </div>
            <div className="text-xl font-bold text-slate-900 mb-1">Booking confirmed</div>
            <div className="text-sm text-slate-500 mb-2">
              Thank you, {d.client.name}. Your appointment is fully secured — no further action needed.
            </div>
            <div className="text-[11px] text-slate-400 mb-5">
              Contract signed {fmtDT(signed.signed_at)} · Deposit paid {fmtDT(signed.deposit_paid_at)}
            </div>
            <a
              href={downloadUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 underline"
              data-testid="sign-client-download-pdf"
            >
              <Download size={13} /> Download signed contract (PDF)
            </a>
          </div>
        </div>
      );
    }

    // ── State 2: reopened after signing, deposit still unpaid ────────────
    if (signed.reopened) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
          <div className="max-w-md w-full bg-white border border-amber-200 rounded-xl p-8 shadow-sm text-center" data-testid="sign-client-pending-payment">
            <div className="mx-auto mb-3 h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center">
              <CheckCircle size={30} weight="fill" className="text-amber-600" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-amber-800 font-bold mb-1">Contract signed</div>
            <div className="text-xl font-bold text-slate-900 mb-1">One more step</div>
            <div className="text-sm text-slate-600 mb-1">
              Your Interpreter Services Agreement has been signed. Please {isDeposit ? "pay your deposit" : "complete payment"} to lock in your appointment.
            </div>
            <div className="text-[11px] text-slate-400 mb-5">
              Signed {fmtDT(signed.signed_at)} · Contract {signed.contract_number}
            </div>

            {payUrl ? (
              <a
                href={payUrl}
                className="inline-flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-lg bg-[#1E3A8A] text-white text-base font-bold hover:bg-[#1E40AF] active:bg-[#1E293B] shadow-sm"
                data-testid="sign-client-pay-deposit-reopened"
              >
                <CreditCard size={18} weight="fill" /> {payLabel} — {fmtMoney(chargeNow)}
              </a>
            ) : (
              <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-4" data-testid="sign-client-no-pay-url">
                Payment link is being generated. Please refresh this page in a few seconds.
              </div>
            )}

            <div className="mt-4">
              <a
                href={downloadUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 underline"
                data-testid="sign-client-download-pdf"
              >
                <Download size={13} /> Download signed contract (PDF)
              </a>
            </div>
          </div>
        </div>
      );
    }

    // ── State 3: just signed in this session ─────────────────────────────
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 shadow-sm text-center" data-testid="sign-client-success">
          <CheckCircle size={40} weight="fill" className="text-emerald-500 mx-auto mb-3" />
          <div className="text-xl font-bold text-slate-900 mb-1">Agreement signed</div>
          <div className="text-sm text-slate-500 mb-4">
            Thank you, {d.client.name}. A signed copy has been emailed to {d.client.email}.
          </div>

          {payUrl && (
            <div className="mb-3">
              <a
                href={payUrl}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-[#1E3A8A] text-white text-sm font-semibold hover:bg-[#1E40AF] w-full justify-center"
                data-testid="sign-client-pay-now"
              >
                <CreditCard size={16} /> Pay now to secure your appointment
              </a>
              {redirectIn !== null && redirectIn > 0 && (
                <div className="text-xs text-slate-400 mt-2" data-testid="sign-client-redirect-countdown">
                  Redirecting to secure payment in {redirectIn}s…
                </div>
              )}
            </div>
          )}

          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 underline"
            data-testid="sign-client-download-pdf"
          >
            <Download size={13} /> Download signed contract (PDF)
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="sign-client-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <img src={LOGO_URL} alt="Unity Language Group" className="h-10 w-auto" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 font-bold">{d.company.name}</div>
            <div className="text-sm text-slate-500">Interpreter Services Agreement</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">Contract</div>
            <div className="font-mono text-sm text-slate-900">{d.contract_number}</div>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Client">
              <Row label="Name" value={d.client.name} />
              {d.client.contact_name && <Row label="Contact" value={d.client.contact_name} />}
              <Row label="Email" value={d.client.email} />
              <Row label="Phone" value={d.client.phone} />
              <Row label="Address" value={d.client.address} />
              {d.client.alien_number && <Row label="A#" value={d.client.alien_number} />}
            </Section>
            <Section title="Appointment">
              <Row label="Type" value={d.project.project_type} />
              <Row label="Language" value={d.project.language} />
              <Row label="Scheduled" value={fmtDT(d.project.scheduled_at)} />
              {d.project.duration_hours && <Row label="Duration" value={`${d.project.duration_hours} hr`} />}
              <Row label="Location" value={d.project.location} />
              {d.project.uscis_case_number && <Row label="USCIS Case #" value={d.project.uscis_case_number} />}
            </Section>
          </div>

          <div className="p-6 bg-slate-50 border-t border-b border-slate-100">
            {d.billing_mode === "hourly_minimum" ? (
              <>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
                    {d.minimum_hours}-hour minimum @ ${d.hourly_rate?.toFixed(2)}/hr
                  </div>
                  <div className="font-display text-2xl font-bold text-slate-900" data-testid="sign-client-min-charge">
                    {fmtMoney(d.minimum_charge || d.amount)} USD
                  </div>
                </div>
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 leading-relaxed" data-testid="sign-client-hourly-notice">
                  <div className="font-bold uppercase tracking-wide text-[10px] mb-1">Extended-time rate</div>
                  <div>
                    If service runs past the {d.minimum_hours}-hour minimum, each additional
                    <b> 30 minutes (or any portion thereof)</b> is billed at
                    <b> ${d.hourly_rate?.toFixed(2)}</b> — the full hourly rate, not prorated.
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Total flat fee</div>
                  <div className="font-display text-2xl font-bold text-slate-900">{fmtMoney(d.amount)} USD</div>
                </div>
                <div className="text-xs text-slate-500">Full duration of the proceeding — no overtime, no hourly add-ons.</div>
              </>
            )}
          </div>

          <div className="p-6 bg-slate-50 text-xs text-slate-700 leading-relaxed border-b border-slate-100" data-testid="client-full-agreement">
            <div className="font-semibold text-slate-900 mb-3 text-sm">Full Interpreter Services Agreement</div>
            <div className="bg-white border border-slate-200 rounded-md p-4 max-h-96 overflow-y-auto space-y-4">
              {(d.full_agreement || []).map((section, i) => {
                const isRefund = /refund|refund policy|cancellation|remboursement|reembolso|ranbousman/i.test(section.heading);
                return (
                  <div key={i} data-testid={`client-agreement-section-${i}`} className={isRefund ? "bg-amber-50 border border-amber-200 rounded-md p-3 -mx-1" : ""}>
                    <div className={`text-[11px] uppercase tracking-wider font-bold mb-1 ${isRefund ? "text-amber-900" : "text-blue-900"}`}>
                      {section.heading}
                    </div>
                    <p className="text-slate-700 leading-relaxed whitespace-pre-line">{section.body}</p>
                    {isRefund && (
                      <div className="mt-3 flex items-center gap-2 pt-2 border-t border-amber-200">
                        <label className="text-[11px] font-semibold text-amber-900 whitespace-nowrap">Initial to acknowledge →</label>
                        <Input
                          data-testid="sign-client-refund-initials"
                          value={refundInitials}
                          onChange={(e) => setRefundInitials(e.target.value.toUpperCase().slice(0, 4))}
                          placeholder="e.g. JB"
                          maxLength={4}
                          className="h-8 w-24 uppercase text-center tracking-widest font-semibold bg-white"
                        />
                        <span className="text-[10px] text-amber-800">Required</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {(!d.full_agreement || d.full_agreement.length === 0) && (
                <p className="text-slate-500">
                  {d.company.name} will provide one qualified professional interpreter for your scheduled proceeding.
                  Payment is required in full to confirm the appointment. 48-hour cancellation policy applies; approved refunds
                  are issued within 5–7 business days. Governed by the laws of the State of {d.company.state}.
                </p>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-3">
              Please read all sections above before signing. A signed PDF + DOCX copy will be emailed to you immediately upon signing.
            </div>
          </div>

          <div className="p-6">
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3">Electronic signature</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs text-slate-700">Type your full legal name</Label>
                <Input data-testid="sign-client-typed-name" value={typedName} onChange={(e) => setTypedName(e.target.value)}
                  placeholder={d.client.contact_name || d.client.name} className="mt-1.5" />
                <div className="text-[11px] text-slate-400 mt-1.5">Legal signature under the ESIGN Act and UETA.</div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs text-slate-700">Draw your signature</Label>
                  <button type="button" onClick={clearCanvas} data-testid="sign-client-clear-canvas"
                    className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1">
                    <ArrowClockwise size={12} /> Clear
                  </button>
                </div>
                <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white">
                  <canvas ref={canvasRef} data-testid="sign-client-canvas"
                    style={{ width: "100%", height: 140, touchAction: "none", cursor: "crosshair" }} />
                </div>
                <div className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
                  <PencilSimple size={11} /> Use your mouse or finger
                </div>
              </div>
            </div>

            <label
              className={`flex items-start gap-3 mt-5 p-4 rounded-lg border-2 cursor-pointer select-none transition-all
                ${agree
                  ? "border-emerald-500 bg-emerald-50 shadow-sm"
                  : "border-amber-300 bg-amber-50 hover:border-amber-400 hover:bg-amber-100 active:bg-amber-200"}`}
              onClick={(e) => {
                // Full-row tap toggles the checkbox — critical for mobile ergonomics.
                if (e.target.tagName !== "INPUT") setAgree((v) => !v);
              }}
              data-testid="sign-client-agree-row"
            >
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 h-6 w-6 shrink-0 cursor-pointer accent-emerald-600"
                data-testid="sign-client-agree"
                aria-label="I confirm I agree to the terms"
              />
              <span className={`text-sm leading-snug font-medium ${agree ? "text-emerald-900" : "text-amber-900"}`}>
                {agree && <span className="mr-1">✓</span>}
                I confirm I am authorized to sign on behalf of {d.client.name}, I have read and agree to
                the Interpreter Services Agreement, and my typed name and drawn signature above are my legal
                electronic signatures.
              </span>
            </label>

            <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                <ShieldCheck size={14} /> IP + timestamp recorded for audit.
              </div>
              <Button data-testid="sign-client-submit" onClick={submit} disabled={submitting}
                className="bg-red-700 hover:bg-red-800 text-white">
                {submitting ? "Signing…" : "Sign agreement"}
              </Button>
            </div>
          </div>
        </div>

        <PublicFooter />
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="text-xs text-slate-400 w-24 shrink-0">{label}</span>
      <span className="text-slate-900">{value || "—"}</span>
    </div>
  );
}
